/**
 * Browser Notification Service
 * 
 * Handles browser notifications using the Web Notifications API.
 * Manages permission requests and displays system-level notifications.
 */

export type NotificationPermission = 'default' | 'granted' | 'denied';

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: unknown;
  requireInteraction?: boolean;
  silent?: boolean;
}

class BrowserNotificationService {
  private permission: NotificationPermission = 'default';

  constructor() {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      this.permission = Notification.permission as NotificationPermission;
    }
  }

  /**
   * Check if browser notifications are supported
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  /**
   * Get current permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission as NotificationPermission;
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Browser notifications are not supported');
    }

    if (this.permission === 'granted') {
      return 'granted';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission as NotificationPermission;
      return this.permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      this.permission = 'denied';
      return 'denied';
    }
  }

  /**
   * Show a browser notification
   */
  async show(options: NotificationOptions): Promise<Notification | null> {
    if (!this.isSupported()) {
      console.warn('Browser notifications are not supported');
      return null;
    }

    // Request permission if not already granted
    if (this.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied');
        return null;
      }
    }

    try {
      const notificationOptions: NotificationOptions = {
        icon: options.icon || '/favicon.ico',
        badge: options.badge || '/favicon.ico',
        tag: options.tag,
        data: options.data,
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        ...options,
      };

      const notification = new Notification(options.title, notificationOptions);

      // Auto-close after 5 seconds (unless requireInteraction is true)
      if (!notificationOptions.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Close a notification by tag
   */
  closeByTag(tag: string): void {
    // Note: The Notification API doesn't provide a direct way to close notifications by tag
    // This would need to be tracked manually if needed
    console.log('Close notification by tag:', tag);
  }

  /**
   * Check if permission is granted
   */
  hasPermission(): boolean {
    return this.getPermission() === 'granted';
  }

  /**
   * Check if permission was denied
   */
  isDenied(): boolean {
    return this.getPermission() === 'denied';
  }
}

// Export singleton instance
export const browserNotificationService = new BrowserNotificationService();

/** localStorage key for "don't show notification permission modal again" */
const NOTIFICATION_PERMISSION_MODAL_STORAGE_KEY = 'notification_permission_modal_dismissed';

/** Whether we should show the custom permission modal (user has not dismissed it permanently). */
export function shouldShowNotificationPermissionModal(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(NOTIFICATION_PERMISSION_MODAL_STORAGE_KEY) !== 'true';
  } catch {
    return true;
  }
}

/** Mark that the user dismissed the permission modal and we should not show it again. */
export function setNotificationPermissionModalDismissed(): void {
  try {
    localStorage.setItem(NOTIFICATION_PERMISSION_MODAL_STORAGE_KEY, 'true');
  } catch {
    // ignore
  }
}
